import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/context/AuthContext';
import { FEEDBACK_ENABLED } from '@/lib/feature-flags';
import { fetchMyFeedback } from '@/lib/supabase-queries';

const DISMISS_KEY = 'feedbackBannerDismissed';

interface FeedbackBannerProps {
  /** Group champion(s) — the leaderboard's top-scoring member(s). Multiple on a
   *  point tie. Used to congratulate by name; falls back to a generic line when empty. */
  winnerNames?: string[];
}

/**
 * End-of-tournament feedback prompt on the group home.
 * Hidden when: the flag is off, no signed-in user, the user already submitted
 * feedback, or they dismissed the banner (persisted in localStorage).
 * Shares the ['feedback', userId] query cache with FeedbackPage, so submitting
 * feedback hides the banner without a reload.
 */
export function FeedbackBanner({ winnerNames = [] }: FeedbackBannerProps) {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(DISMISS_KEY) === '1',
  );

  const { data: existing, isLoading } = useQuery({
    queryKey: ['feedback', user?.id],
    queryFn: () => fetchMyFeedback(user!.id),
    enabled: FEEDBACK_ENABLED && !!user,
  });

  if (!FEEDBACK_ENABLED || !user || dismissed) return null;
  // Don't flash before we know whether they've already responded.
  if (isLoading || existing) return null;

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  // "ברכות ל-X" / "ברכות ל-X ול-Y" for a tie; generic thanks when no winner is known.
  const congrats = winnerNames.length
    ? `ברכות ל-${winnerNames.join(' ול-')} על האליפות 👑 ותודה לכל מי ששיחק.`
    : 'תודה לכל מי ששיחק.';
  const message = `🏆 הפלייאוף הסתיים!\n${congrats}\n2 דקות פידבק יעזרו לנו לבנות טורניר טוב יותר לפעם הבאה.`;

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border border-orange-500/70 bg-orange-500/85 p-4"
      dir="rtl"
    >
      <div className="flex items-start gap-3">
        <p className="flex-1 text-white font-semibold leading-relaxed whitespace-pre-line">
          {message}
        </p>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="סגור"
          className="shrink-0 text-white/40 hover:text-white transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <Link
          to="/feedback"
          className="inline-flex items-center justify-center rounded-xl bg-white px-4 py-2.5 text-sm font-bold text-orange-600 shadow-sm hover:bg-white/90 transition-colors min-h-[44px]"
        >
          למילוי המשוב
        </Link>
        <Link
          to="/tip-jar"
          className="inline-flex items-center justify-center rounded-xl border border-white/80 bg-white/10 px-4 py-2.5 text-sm font-semibold text-white hover:bg-white/20 transition-colors min-h-[44px]"
        >
          לתמוך בפרוייקט
        </Link>
      </div>
    </motion.div>
  );
}

export default FeedbackBanner;
